import { supabase, supabaseAnonKey, supabaseUrl } from './supabaseClient';

export const TEMPLATE_VARIABLES = [
  'event_name', 'event_date', 'event_time', 'venue', 'recipient_name', 'prep_staff_name',
  'equipment_list', 'quote_number', 'event_reference'
] as const;

export type RecipientRule = 'ASSIGNED_PREP_STAFF' | 'EVENT_MANAGER' | 'AUTHENTICATED_ADMIN' | 'FIXED_INTERNAL_STAFF';
export type NotificationTypeSummary = {
  code: string;
  label: string;
  description: string;
  configurable: boolean;
  channel?: 'EMAIL' | 'SMS' | 'BOTH';
  status: 'ENABLED' | 'SHADOW_ENABLED' | 'DISABLED' | 'NOT_CONFIGURED';
  version: number;
};

export type NotificationConfig = {
  enabled: boolean;
  channel: 'EMAIL' | 'SMS' | 'BOTH';
  timing_value: number;
  timing_unit: 'DAYS' | 'HOURS';
  timing_anchor: 'EVENT_START' | 'PREP_DUE';
  follow_up_channel?: 'EMAIL' | 'SMS' | 'BOTH' | null;
  follow_up_enabled: boolean;
  follow_up_value: number | null;
  follow_up_unit: 'DAYS' | 'HOURS' | null;
  primary_recipient_rule: RecipientRule;
  primary_fixed_staff_id: string | null;
  fallback_recipient_rule: Exclude<RecipientRule, 'ASSIGNED_PREP_STAFF'> | null;
  fallback_fixed_staff_id: string | null;
  email_subject: string;
  email_body: string;
  sms_body: string;
  include_event_details: boolean;
  include_equipment_list: boolean;
  include_venue: boolean;
  include_event_datetime: boolean;
};

export type LoadedNotificationConfig = NotificationConfig & { id: string; version: number; created_at: string };
export type NotificationPreview = {
  subject: string;
  body: string;
  content_hash: string;
  sms_body: string;
  sms_content_hash: string;
  sample: boolean;
};

export type SmsTestReadiness = {
  available: boolean;
  reason: string | null;
  recipient: string | null;
  masked_mobile: string | null;
};

export type SmsTestResult = {
  test_run_id: string;
  state: 'PROVIDER_ACCEPTED' | 'FAILED' | 'UNCERTAIN';
  provider_status: string | null;
  provider_message_id: string | null;
  attempt_count: number;
  closed: boolean;
  reconciliation_required: boolean;
};
export type NotificationHistoryRow = {
  id: string;
  notification_code: string;
  notification_type: string;
  event: string;
  checkpoint: string | null;
  recipient: string;
  scheduled_at: string | null;
  sent_at: string | null;
  status: 'SCHEDULED' | 'SENT' | 'SKIPPED' | 'FAILED' | 'CANCELLED';
  provider_message_id: string | null;
  recipient_email: string | null;
  config_version: number;
  mode: 'SHADOW' | 'LIVE';
  delivery_label: 'SHADOW / NOT SENT' | 'LIVE' | 'LIVE / PROVIDER ACCEPTED';
  skip_reason: string | null;
  live_eligible: boolean;
  channel?: 'EMAIL' | 'SMS';
};

export type ControlledLiveResult = {
  permit_id: string;
  state: 'PROVIDER_ACCEPTED' | 'FAILED' | 'UNCERTAIN';
  provider_message_id: string | null;
  attempt_started: boolean;
  closed: boolean;
  reconciliation_required: boolean;
};

export type ContractorControlledLiveResult = {
  permit_id: string;
  state: 'PROVIDER_ACCEPTED' | 'FAILED' | 'UNCERTAIN';
  provider_status: string | null;
  provider_message_id: string | null;
  provider_http_status: number | null;
  provider_parts: number | null;
  attempt_count: number;
  closed: boolean;
  reconciliation_required: boolean;
};

export type NotificationDispatchStatus = {
  dispatcher_enabled: boolean;
  equipment_prep_email_enabled: boolean;
  equipment_prep_sms_enabled?: boolean;
  equipment_prep_follow_up_enabled?: boolean;
  equipment_prep_sms_live_from?: string | null;
  equipment_prep_follow_up_live_from?: string | null;
  contractor_arrived_sms_enabled: boolean;
  live_from: string | null;
  updated_at: string;
  equipment_prep_config_version: number;
  contractor_status_config_version: number;
  schedule: string;
  staff_assignment_email_enabled: boolean;
  staff_assignment_sms_enabled: boolean;
  staff_assignment_live_from: string | null;
  staff_assignment_config_version: number;
};

export const DEFAULT_EQUIPMENT_PREP_CONFIG: NotificationConfig = {
  enabled: false,
  channel: 'EMAIL',
  timing_value: 3,
  timing_unit: 'DAYS',
  timing_anchor: 'EVENT_START',
  follow_up_channel: 'EMAIL',
  follow_up_enabled: false,
  follow_up_value: null,
  follow_up_unit: null,
  primary_recipient_rule: 'ASSIGNED_PREP_STAFF',
  primary_fixed_staff_id: null,
  fallback_recipient_rule: 'AUTHENTICATED_ADMIN',
  fallback_fixed_staff_id: null,
  email_subject: 'Equipment PREP — {{event_name}}',
  email_body: `Hi {{recipient_name}},

Please complete equipment PREP for {{event_name}}.

Event: {{event_name}}
Date: {{event_date}} {{event_time}}
Venue: {{venue}}

Equipment:
{{equipment_list}}

Reference: {{event_reference}}`,
  sms_body: 'Hi {{recipient_name}}, Equipment PREP for {{event_name}} is due before {{event_date}} {{event_time}}. {{venue}}',
  include_event_details: true,
  include_equipment_list: true,
  include_venue: true,
  include_event_datetime: true
};

const variablePattern = /\{\{\s*([a-z_]+)\s*\}\}/g;

export type NotificationTemplateContext = Partial<Record<typeof TEMPLATE_VARIABLES[number], string>>;

export function renderNotificationTemplate(value: string, context: NotificationTemplateContext): string {
  const unknown = unknownTemplateVariables(value);
  if (unknown.length) throw new Error(`Unknown template variable: ${unknown.join(', ')}.`);

  return value
    .split('\n')
    .filter(line => {
      const names = Array.from(line.matchAll(variablePattern), match => match[1] as keyof NotificationTemplateContext);
      return names.length === 0 || names.some(name => Boolean(context[name]?.trim()));
    })
    .map(line => line.replace(variablePattern, (_, name: keyof NotificationTemplateContext) => context[name] || '').trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function unknownTemplateVariables(value: string): string[] {
  const names = Array.from(value.matchAll(variablePattern), match => match[1]);
  const unknown = names.filter(name => !(TEMPLATE_VARIABLES as readonly string[]).includes(name));
  const withoutKnownTokens = value.replace(variablePattern, '');
  if (withoutKnownTokens.includes('{{') || withoutKnownTokens.includes('}}')) unknown.push('malformed variable');
  return Array.from(new Set(unknown));
}

export function validateNotificationConfig(config: NotificationConfig): string[] {
  const errors: string[] = [];
  if (!['EMAIL', 'SMS', 'BOTH'].includes(config.channel)) errors.push('Select Email, SMS, or Both.');
  if (config.follow_up_enabled && !['EMAIL','SMS','BOTH'].includes(config.follow_up_channel || 'EMAIL')) errors.push('Select a valid follow-up channel.');
  if (!Number.isInteger(config.timing_value) || config.timing_value < 0 || config.timing_value > 365) errors.push('Timing must be between 0 and 365.');
  if (!config.email_subject.trim() || config.email_subject.length > 250 || /[\r\n]/.test(config.email_subject)) errors.push('Subject must be one line and 250 characters or fewer.');
  if (!config.email_body.trim() || config.email_body.length > 20000) errors.push('Email body is required and must be 20,000 characters or fewer.');
  if ((config.channel === 'SMS' || config.channel === 'BOTH' || (config.follow_up_enabled && ['SMS','BOTH'].includes(config.follow_up_channel || 'EMAIL'))) && (!config.sms_body.trim() || config.sms_body.length > 1600)) errors.push('SMS body is required and must be 1,600 characters or fewer.');
  const unknown = [...unknownTemplateVariables(config.email_subject), ...unknownTemplateVariables(config.email_body), ...unknownTemplateVariables(config.sms_body || '')];
  if (unknown.length) errors.push(`Unknown template variable: ${Array.from(new Set(unknown)).join(', ')}.`);
  if (config.primary_recipient_rule === 'EVENT_MANAGER' || config.fallback_recipient_rule === 'EVENT_MANAGER') errors.push('Event Manager is unavailable until the CRM has an authoritative event-manager field.');
  if (config.primary_recipient_rule === 'FIXED_INTERNAL_STAFF' && !config.primary_fixed_staff_id) errors.push('Select a fixed primary staff member.');
  if (config.fallback_recipient_rule === 'FIXED_INTERNAL_STAFF' && !config.fallback_fixed_staff_id) errors.push('Select a fixed fallback staff member.');
  if (config.follow_up_enabled && (!Number.isInteger(config.follow_up_value) || (config.follow_up_value || 0) < 1 || (config.follow_up_value || 0) > 365 || !config.follow_up_unit)) errors.push('Follow-up timing must be between 1 and 365.');
  return errors;
}

function rows<T>(data: unknown): T[] {
  if (!Array.isArray(data)) return [];
  return data.flatMap(item => item && typeof item === 'object' && Object.keys(item).length === 1 && 'jsonb_build_object' in item
    ? [(item as any).jsonb_build_object as T] : [item as T]);
}

export async function listNotificationTypes(): Promise<NotificationTypeSummary[]> {
  const { data, error } = await supabase.rpc('admin_notification_types');
  if (error) throw new Error(error.message);
  return rows<NotificationTypeSummary>(data);
}

export async function loadNotificationConfig(code: string): Promise<LoadedNotificationConfig | null> {
  const { data, error } = await supabase.rpc('admin_notification_config', { p_type_code: code });
  if (error) throw new Error(error.message);
  return data ? data as LoadedNotificationConfig : null;
}

export async function saveNotificationConfig(code: string, expectedVersion: number, config: NotificationConfig): Promise<{ id: string; version: number; enabled: boolean }> {
  const errors = validateNotificationConfig(config);
  if (errors.length) throw new Error(errors.join(' '));
  const { data, error } = await supabase.rpc('admin_save_notification_config', {
    p_type_code: code,
    p_expected_version: expectedVersion,
    p_config: config
  });
  if (error) throw new Error(error.message);
  return data as { id: string; version: number; enabled: boolean };
}

export async function previewNotification(code: string, config: NotificationConfig): Promise<NotificationPreview> {
  const errors = validateNotificationConfig(config);
  if (errors.length) throw new Error(errors.join(' '));
  const { data, error } = await supabase.rpc('admin_preview_notification', {
    p_type_code: code,
    p_config: config,
    p_shift: null
  });
  if (error) throw new Error(error.message);
  return data as NotificationPreview;
}

export async function loadSmsTestReadiness(): Promise<SmsTestReadiness> {
  const { data, error } = await supabase.rpc('admin_notification_sms_test_readiness');
  if (error) throw new Error(error.message);
  return data as SmsTestReadiness;
}

export async function sendTestSmsToMe(code: string, smsBody: string): Promise<SmsTestResult> {
  // The SMS test is deliberately more explicit than ordinary settings reads: after a
  // restored browser session, make sure the Supabase client has attached a current
  // authenticated token before the write-capable preparation RPC is allowed to run.
  const refreshed = await supabase.auth.refreshSession();
  if (refreshed.error || !refreshed.data.session?.access_token) {
    throw new Error('A current authenticated Admin session is required for the SMS test.');
  }
  const prepared = await supabase.rpc('admin_prepare_notification_sms_test', {
    p_type_code: code,
    p_sms_body: smsBody
  });
  if (prepared.error) throw new Error(prepared.error.message);
  const testRunId = (prepared.data as { test_run_id?: string } | null)?.test_run_id;
  if (!testRunId) throw new Error('The Tom-only SMS test run was not prepared.');

  const armed = await supabase.rpc('admin_arm_notification_sms_test', { p_test_run: testRunId });
  if (armed.error) throw new Error(armed.error.message);
  if ((armed.data as { armed?: boolean } | null)?.armed !== true) throw new Error('The Tom-only SMS test run was not armed.');

  const delivery = await supabase.functions.invoke('notification-sms-test/send', {
    body: { test_run_id: testRunId }
  });
  if (delivery.error) throw new Error(`SMS test did not return a confirmed result: ${delivery.error.message}`);
  return delivery.data as SmsTestResult;
}

export async function loadNotificationHistory(): Promise<NotificationHistoryRow[]> {
  const [existing, assignments] = await Promise.all([
    supabase.rpc('admin_notification_history', { p_limit: 100, p_offset: 0 }),
    supabase.rpc('admin_staff_assignment_history', { p_limit: 100, p_offset: 0 })
  ]);
  if (existing.error) throw new Error(existing.error.message);
  if (assignments.error) throw new Error(assignments.error.message);
  // Both RPCs return notification_stages.id. Merge by that durable identity,
  // never by event, recipient, send time or provider message ID.
  const history = new Map<string, NotificationHistoryRow>();
  for (const row of rows<NotificationHistoryRow>(existing.data)) {
    history.set(`${row.notification_code}:${row.id}`, row);
  }
  for (const row of rows<NotificationHistoryRow>(assignments.data)) {
    const key = `${row.notification_code}:${row.id}`;
    const previous = history.get(key);
    history.set(key, {
      ...previous,
      ...row,
      checkpoint: row.checkpoint && row.channel ? `${row.checkpoint} / ${row.channel}` : row.checkpoint,
      recipient: row.recipient_email
        ? `${row.recipient} — ${row.recipient_email}`
        : previous?.recipient || row.recipient
    });
  }
  return Array.from(history.values())
    .sort((left, right) => String(right.scheduled_at || '').localeCompare(String(left.scheduled_at || '')))
    .slice(0, 100);
}

export async function loadNotificationDispatchStatus(): Promise<NotificationDispatchStatus> {
  const { data, error } = await supabase.rpc('admin_notification_dispatch_status');
  if (error) throw new Error(error.message);
  return data as NotificationDispatchStatus;
}

export async function disableAutomaticNotificationDelivery(expectedUpdatedAt: string): Promise<NotificationDispatchStatus> {
  const { data, error } = await supabase.rpc('admin_disable_all_notification_dispatch', {
    p_expected_updated_at: expectedUpdatedAt
  });
  if (error) throw new Error(error.message);
  return data as NotificationDispatchStatus;
}

async function invokeAuthenticatedNotificationPath<T>(path: string, body: Record<string, string>): Promise<T> {
  const refreshed = await supabase.auth.refreshSession();
  const accessToken = refreshed.data.session?.access_token;
  if (refreshed.error || !accessToken) throw new Error('A current authenticated Admin session is required.');

  const response = await fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/${path}`, {
    method: 'POST',
    headers: {
      apikey: supabaseAnonKey,
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
      'x-client-info': 'quikpix-crm-notification-admin'
    },
    body: JSON.stringify(body),
    credentials: 'omit',
    redirect: 'error'
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result) {
    throw new Error(`Notification delivery did not return a confirmed result (HTTP ${response.status}).`);
  }
  return result as T;
}

export async function sendControlledEquipmentPrep(stageId: string): Promise<ControlledLiveResult> {
  const prepared = await supabase.rpc('admin_prepare_equipment_prep_live', { p_stage: stageId });
  if (prepared.error) throw new Error(prepared.error.message);
  const permitId = (prepared.data as { permit_id?: string } | null)?.permit_id;
  if (!permitId) throw new Error('The controlled live permit was not created.');

  const armed = await supabase.rpc('admin_arm_equipment_prep_live', { p_permit: permitId });
  if (armed.error) throw new Error(armed.error.message);
  if ((armed.data as { armed?: boolean } | null)?.armed !== true) throw new Error('The exact-stage permit was not armed.');

  return invokeAuthenticatedNotificationPath<ControlledLiveResult>(
    'notification-equipment-prep-live/send',
    { permit_id: permitId }
  );
}

export async function sendControlledContractorStatus(stageId: string): Promise<ContractorControlledLiveResult> {
  const prepared = await supabase.rpc('admin_prepare_contractor_status_live', { p_stage: stageId });
  if (prepared.error) throw new Error(prepared.error.message);
  const permitId = (prepared.data as { permit_id?: string } | null)?.permit_id;
  if (!permitId) throw new Error('The contractor stage permit was not created.');

  const armed = await supabase.rpc('admin_arm_contractor_status_live', { p_permit: permitId });
  if (armed.error) throw new Error(armed.error.message);
  if ((armed.data as { armed?: boolean } | null)?.armed !== true) throw new Error('The contractor stage permit was not armed.');

  return invokeAuthenticatedNotificationPath<ContractorControlledLiveResult>(
    'notification-contractor-status-live/send',
    { permit_id: permitId }
  );
}

export async function sendControlledStaffAssignment(stageId: string, channel: 'EMAIL' | 'SMS') {
  const prepared = await supabase.rpc('admin_prepare_staff_assignment_live', { p_stage: stageId });
  if (prepared.error) throw new Error(prepared.error.message);
  const permitId = (prepared.data as { permit_id?: string } | null)?.permit_id;
  if (!permitId) throw new Error('The assignment stage permit was not created.');
  const armed = channel === 'EMAIL'
    ? await supabase.rpc('admin_arm_equipment_prep_live', { p_permit: permitId })
    : await supabase.rpc('admin_arm_contractor_status_live', { p_permit: permitId });
  if (armed.error) throw new Error(armed.error.message);
  if ((armed.data as { armed?: boolean } | null)?.armed !== true) throw new Error('The assignment stage permit was not armed.');
  return channel === 'EMAIL'
    ? invokeAuthenticatedNotificationPath<ControlledLiveResult>('notification-equipment-prep-live/send', { permit_id: permitId })
    : invokeAuthenticatedNotificationPath<ContractorControlledLiveResult>('notification-contractor-status-live/send', { permit_id: permitId });
}
